import { useEffect, useState } from 'react'
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { loadStripe, type StripeError } from '@stripe/stripe-js'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useCreateSetupIntent,
  useSetDefaultPaymentMethod,
} from '@/api/stripe'

/**
 * Dialog Stripe Elements pour ajouter / mettre à jour la carte de paiement
 * par défaut. Aucun paiement n'est effectué ici : c'est juste un SetupIntent
 * qui sauvegarde la CB sur le Customer Stripe.
 *
 * Utilisé par :
 *  - `PaymentMethodCard` (page Billing) pour le flow MAJ CB normal
 *  - `ConfirmPlanChangeDialog` en fallback quand un paiement échoue (au lieu
 *    de rediriger vers Stripe Checkout).
 *
 * Sur succès, le composant parent reçoit le callback `onSuccess()` et peut
 * retry l'action qui avait échoué (ex: re-tenter create-subscription-inline).
 */
const stripePromise = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)
  : null

interface Props {
  open: boolean
  onClose: () => void
  /** Appelé après MAJ CB réussie (avant la fermeture du dialog). */
  onSuccess?: () => void | Promise<void>
  darkMode?: boolean
  /** Sous-titre custom (sinon défaut "votre nouvelle carte sera utilisée..."). */
  description?: string
}

export function UpdateCardDialog({
  open,
  onClose,
  onSuccess,
  darkMode = false,
  description,
}: Props) {
  const { t } = useTranslation()
  const createSetup = useCreateSetupIntent()
  const [clientSecret, setClientSecret] = useState<string | null>(null)

  // Un seul appel par ouverture : sans ce verrou, un échec relançait
  // l'effet à chaque rendu (createSetup change d'identité) et l'API
  // était appelée en boucle.
  const [demande, setDemande] = useState(false)

  useEffect(() => {
    if (!open) {
      setClientSecret(null)
      setDemande(false)
      return
    }
    if (demande) return
    setDemande(true)
    createSetup.mutate(undefined, {
      onSuccess: (data) => setClientSecret(data.client_secret),
      onError: () => {
        toast.error(
          t(
            'profile.billing.setupError',
            "Impossible d'initialiser la mise à jour.",
          ),
        )
        onClose()
      },
    })
  }, [open, demande, createSetup, t, onClose])

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className={cn(
          // Le formulaire Stripe (carte + Link) est haut : on borne la modale
          // à l'écran et on fait défiler l'intérieur, pour que Annuler et
          // Enregistrer restent atteignables sans dézoomer.
          'sm:max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto',
          darkMode && 'bg-slate-900 border-slate-700 text-slate-100',
        )}
      >
        <DialogHeader>
          <DialogTitle>
            {t('profile.billing.updateCardTitle', 'Mettre à jour la carte')}
          </DialogTitle>
          <DialogDescription>
            {description ??
              t(
                'profile.billing.updateCardDescription',
                'Votre nouvelle carte sera utilisée pour les prochains paiements.',
              )}
          </DialogDescription>
        </DialogHeader>

        {!stripePromise ? (
          <p className="text-sm text-red-500">
            {t('profile.billing.stripeKeyMissing', 'Clé Stripe absente.')}
          </p>
        ) : !clientSecret ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        ) : (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: { theme: darkMode ? 'night' : 'stripe' },
            }}
          >
            <UpdateCardForm
              onDone={async () => {
                if (onSuccess) await onSuccess()
                onClose()
              }}
            />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  )
}

function UpdateCardForm({ onDone }: { onDone: () => void | Promise<void> }) {
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()
  const setDefault = useSetDefaultPaymentMethod()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!stripe || !elements) return
    setSubmitting(true)
    setError(null)
    try {
      const result = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      })
      if (result.error) {
        throw new Error(result.error.message || 'Setup refusé')
      }
      const pmId =
        typeof result.setupIntent?.payment_method === 'string'
          ? result.setupIntent.payment_method
          : result.setupIntent?.payment_method?.id
      if (!pmId) throw new Error('PaymentMethod ID manquant')
      await setDefault.mutateAsync(pmId)
      toast.success(t('profile.billing.cardUpdated', 'Carte mise à jour'))
      await onDone()
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : (err as StripeError)?.message || 'Erreur'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        void handleSubmit()
      }}
      className="space-y-4"
    >
      <PaymentElement
        options={{
          // Une carte, rien d'autre : pas d'onglet Google Pay, pas de mention
          // légale Stripe (la nôtre est sous le bouton). Canada proposé par
          // défaut, mais le pays reste au choix : forcé, il bloquait les
          // cartes étrangères sur le code postal (client à Oman, 12 oct. 2026).
          layout: 'tabs',
          wallets: { applePay: 'never', googlePay: 'never' },
          terms: { card: 'never' },
          defaultValues: { billingDetails: { address: { country: 'CA' } } },
        }}
      />
      {error && <p className="text-sm text-red-500">{error}</p>}
      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => void onDone()}
          disabled={submitting}
        >
          {t('common.cancel', 'Annuler')}
        </Button>
        <Button
          type="submit"
          disabled={!stripe || !elements || submitting}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : null}
          {t('profile.billing.save', 'Sauvegarder')}
        </Button>
      </div>
    </form>
  )
}
